import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, onSnapshot, doc, updateDoc, deleteDoc, addDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { confirmReactivate, confirmDeleteClient } from '../services/clientDupes';

const STANDARD_SERVICES = [
  { key: 'Blog Optimization', label: 'Blog Optimization' },
  { key: 'Social Posts',      label: 'Social Posts' },
  { key: 'Video Generation',  label: 'Video Generation' },
  { key: 'Press Release',     label: 'Press Release' },
  { key: 'Report Summary',    label: 'Report Summary' },
];
const DAYS = ['Su','M','T','W','Th','F','Sa'];
const DAY_LABELS = { Su:'Sun', M:'Mon', T:'Tue', W:'Wed', Th:'Thu', F:'Fri', Sa:'Sat' };

// Defined outside the component so React sees a stable type on every render
function ClientTable({ rows, archived, onSelect, onEdit, onArchive, onRestore, onDelete, onAddClient }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Business Name</th>
            <th>URL</th>
            <th>Services</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.id}>
              <td>
                {archived
                  ? <span style={{ color: 'var(--text-secondary)' }}>{c.businessName}</span>
                  : <strong style={{ cursor: 'pointer', color: 'var(--accent-blue)' }} onClick={() => onSelect(c)}>{c.businessName}</strong>
                }
              </td>
              <td>{c.clientUrl && <a href={c.clientUrl} target="_blank" rel="noreferrer">{c.clientUrl}</a>}</td>
              <td style={{ maxWidth: 300 }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                  {(c.services || []).map(s => typeof s === 'string' ? s : s.name || s.label || s.type || '').join(', ')}
                </span>
              </td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  {archived ? (
                    <button className="btn btn-sm btn-success" onClick={() => onRestore(c)}>↩ Restore</button>
                  ) : (
                    <>
                      <button className="btn btn-sm btn-secondary" onClick={() => onEdit(c)}>✏️</button>
                      <button className="btn btn-sm btn-danger" onClick={() => onArchive(c.id)}>Archive</button>
                    </>
                  )}
                  <button className="btn btn-sm btn-danger" onClick={() => onDelete(c)}>🗑</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="empty-state">
          {archived
            ? 'No archived clients.'
            : <><span>No active clients.</span> <button className="btn btn-primary btn-sm" onClick={onAddClient}>Add your first client</button></>
          }
        </div>
      )}
    </div>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients]               = useState([]);
  const [archivedClients, setArchivedClients] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [editClient, setEditClient]         = useState(null);
  const [saving, setSaving]                 = useState(false);
  const [showArchived, setShowArchived]     = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'clients'), snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a,b) => (a.businessName||'').localeCompare(b.businessName||''));
      setClients(all.filter(c => !c.isArchived));
      setArchivedClients(all.filter(c => c.isArchived));
      setLoading(false);
    });
    return unsub;
  }, []);

  const archiveClient = async (id) => {
    if (window.confirm('Archive this client?')) {
      await updateDoc(doc(db, 'clients', id), { isArchived: true });
    }
  };

  const restoreClient = async (client) => {
    if (!(await confirmReactivate(client))) return;
    await updateDoc(doc(db, 'clients', client.id), { isArchived: false });
  };

  const deleteClient = async (client) => {
    if (!confirmDeleteClient(client)) return;
    await deleteDoc(doc(db, 'clients', client.id));
  };

  const printClientList = () => {
    const sorted = [...clients].sort((a,b) => (a.businessName||'').localeCompare(b.businessName||''));
    const rows = sorted.map(c => `
      <div class="row">
        <span class="box"></span>
        <span class="name">${c.businessName || ''}</span>
      </div>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Client List</title>
      <style>
        body{font-family:Arial,sans-serif;padding:48px 56px;color:#000;max-width:720px;margin:0 auto}
        h1{font-size:20px;font-weight:700;margin-bottom:6px}
        .title-line{border-bottom:2px solid #000;width:340px;display:inline-block;margin-left:8px;vertical-align:middle}
        .divider{border:none;border-top:1px solid #ccc;margin:20px 0}
        .row{display:flex;align-items:center;gap:14px;padding:9px 0;border-bottom:1px solid #e5e5e5}
        .box{width:18px;height:18px;border:2px solid #333;display:inline-block;flex-shrink:0}
        .name{font-size:14px}
        @media print{body{padding:24px 32px}}
      </style></head><body>
      <h1>Title: <span class="title-line"></span></h1>
      <hr class="divider"/>
      ${rows}
      <script>window.onload=function(){window.print()}<\/script>
    </body></html>`);
    w.document.close();
  };

  const handleEdit = async () => {
    if (!editClient) return;
    setSaving(true);
    const { id, ...data } = editClient;
    data.noContentQueue = !(data.services || []).includes('Blog Optimization');
    // Only stamp when the brand voice text actually changed — a stamp on every
    // unrelated profile save would make the notes look freshly reviewed to
    // get_client_brand_kit when nobody read them.
    const before = [...clients, ...archivedClients].find(c => c.id === id);
    if (before && (before.brandVoice || '') !== (data.brandVoice || '')) {
      data.brandVoiceUpdatedAt = serverTimestamp();
    }
    await updateDoc(doc(db, 'clients', id), data);
    await generateServiceTasks(id, data.services || []);
    setEditClient(null);
    setSaving(false);
  };

  const generateServiceTasks = async (clientId, services) => {
    const getExisting = async (taskType) => {
      const snap = await getDocs(query(collection(db, 'tasks'), where('clientId', '==', clientId), where('taskType', '==', taskType)));
      const keys = new Set();
      snap.forEach(t => {
        const d = t.data().dueDate?.toDate ? t.data().dueDate.toDate() : null;
        if (d) keys.add(`${d.getFullYear()}_${d.getMonth()}_${d.getDate()}`);
      });
      return keys;
    };
    const save = (taskType, dueDate) => addDoc(collection(db, 'tasks'), {
      clientId, title: taskType, taskType, dueDate, completed: false, createdAt: serverTimestamp(),
    });

    const now = new Date();

    // Social Posts — every Tuesday
    if (services.includes('Social Posts')) {
      const existing = await getExisting('Social Posts');
      const end = new Date(now); end.setMonth(end.getMonth() + 6);
      let d = new Date(now); d.setHours(12, 0, 0, 0);
      while (d.getDay() !== 2) d.setDate(d.getDate() + 1);
      while (d <= end) {
        const key = `${d.getFullYear()}_${d.getMonth()}_${d.getDate()}`;
        if (!existing.has(key)) await save('Social Posts', new Date(d));
        d = new Date(d); d.setDate(d.getDate() + 7);
      }
    }

    // Report Summary — 1st of each month
    if (services.includes('Report Summary')) {
      const existing = await getExisting('Report Summary');
      for (let i = 0; i <= 6; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1, 12, 0, 0);
        if (d > now) {
          const key = `${d.getFullYear()}_${d.getMonth()}_${d.getDate()}`;
          if (!existing.has(key)) await save('Report Summary', d);
        }
      }
    }

    // Press Release — last day of each month
    if (services.includes('Press Release')) {
      const existing = await getExisting('Press Release');
      for (let i = 0; i <= 6; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i + 1, 0, 12, 0, 0);
        if (d > now) {
          const key = `${d.getFullYear()}_${d.getMonth()}_${d.getDate()}`;
          if (!existing.has(key)) await save('Press Release', d);
        }
      }
    }

    // Video Generation — 2nd Friday of each month
    if (services.includes('Video Generation')) {
      const existing = await getExisting('Video Generation');
      for (let i = 0; i <= 6; i++) {
        let d = new Date(now.getFullYear(), now.getMonth() + i, 1, 12, 0, 0);
        while (d.getDay() !== 5) d.setDate(d.getDate() + 1);
        d.setDate(d.getDate() + 7);
        if (d > now) {
          const key = `${d.getFullYear()}_${d.getMonth()}_${d.getDate()}`;
          if (!existing.has(key)) await save('Video Generation', new Date(d));
        }
      }
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner"></div></div>;

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div className="page-title" style={{ marginBottom: 0 }}>All Clients</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {archivedClients.length > 0 && (
            <button className="btn btn-secondary" onClick={() => setShowArchived(s => !s)}>
              {showArchived ? '▲ Hide Archived' : `▼ Archived (${archivedClients.length})`}
            </button>
          )}
          <button className="btn btn-secondary" onClick={printClientList}>🖨️ Print List</button>
          <button className="btn btn-primary" onClick={() => navigate('/client-setup')}>+ Add Client</button>
        </div>
      </div>

      <ClientTable
        rows={clients}
        archived={false}
        onSelect={c => navigate('/clients/' + c.id)}
        onEdit={c => setEditClient({ ...c, services: c.services || [], blogDays: c.blogDays || [], imageStyle: c.imageStyle || 'illustrated', autoPublish: c.autoPublish ?? false })}
        onArchive={archiveClient}
        onRestore={restoreClient}
        onDelete={deleteClient}
        onAddClient={() => navigate('/client-setup')}
      />

      {showArchived && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            Archived Clients
          </div>
          <ClientTable
            rows={archivedClients}
            archived={true}
            onSelect={c => navigate('/clients/' + c.id)}
            onEdit={c => setEditClient({ ...c, services: c.services || [], blogDays: c.blogDays || [], imageStyle: c.imageStyle || 'illustrated', autoPublish: c.autoPublish ?? false })}
            onArchive={archiveClient}
            onRestore={restoreClient}
            onDelete={deleteClient}
            onAddClient={() => navigate('/client-setup')}
          />
        </div>
      )}

      {editClient && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditClient(null)}>
          <div className="modal" style={{ maxWidth: 620, maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-title">Edit Client</div>

            <div className="form-field">
              <label>Business Name</label>
              <input type="text" value={editClient.businessName || ''} onChange={e => setEditClient(c => ({ ...c, businessName: e.target.value }))} />
            </div>
            <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div className="form-field">
                <label>Contact Name</label>
                <input type="text" value={editClient.contactName || ''} onChange={e => setEditClient(c => ({ ...c, contactName: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Contact Email</label>
                <input type="email" value={editClient.contactEmail || ''} onChange={e => setEditClient(c => ({ ...c, contactEmail: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Contact Phone</label>
                <input type="tel" placeholder="e.g. (555) 555-5555" value={editClient.contactPhone || ''} onChange={e => setEditClient(c => ({ ...c, contactPhone: e.target.value }))} />
              </div>
            </div>
            <div className="form-field">
              <label>Client URL</label>
              <input type="url" value={editClient.clientUrl || ''} onChange={e => setEditClient(c => ({ ...c, clientUrl: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Claude Project Link</label>
                <input type="url" value={editClient.gptLink || ''} onChange={e => setEditClient(c => ({ ...c, gptLink: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Google Drive Link</label>
                <input type="url" value={editClient.driveLink || ''} onChange={e => setEditClient(c => ({ ...c, driveLink: e.target.value }))} />
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Services</div>
              {STANDARD_SERVICES.map(s => (
                <div key={s.key}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      checked={(editClient.services || []).includes(s.key)}
                      onChange={() => setEditClient(c => ({
                        ...c,
                        services: (c.services || []).includes(s.key)
                          ? (c.services || []).filter(x => x !== s.key)
                          : [...(c.services || []), s.key]
                      }))}
                    />
                    {s.label}
                  </label>
                  {s.key === 'Blog Optimization' && (editClient.services || []).includes('Blog Optimization') && (
                    <div style={{ marginLeft: 24, marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>Blog schedule days:</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {DAYS.map(d => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setEditClient(c => ({
                              ...c,
                              blogDays: (c.blogDays || []).includes(d)
                                ? (c.blogDays || []).filter(x => x !== d)
                                : [...(c.blogDays || []), d]
                            }))}
                            style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: '1px solid', cursor: 'pointer', background: (editClient.blogDays || []).includes(d) ? '#0a1f12' : 'var(--bg-primary)', borderColor: (editClient.blogDays || []).includes(d) ? '#16a34a55' : 'var(--border)', color: (editClient.blogDays || []).includes(d) ? '#4ade80' : 'var(--text-muted)' }}
                          >
                            {DAY_LABELS[d]}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Content Settings</div>
              <div className="form-field">
                <label>WordPress Publisher API Key</label>
                <input type="text" value={editClient.wpApiKey || ''} onChange={e => setEditClient(c => ({ ...c, wpApiKey: e.target.value }))} style={{ fontFamily: 'monospace', fontSize: 12 }} placeholder="c6pk_..." />
              </div>
              <div className="form-field">
                <label>WP Username</label>
                <input type="text" value={editClient.wpUsername || ''} onChange={e => setEditClient(c => ({ ...c, wpUsername: e.target.value }))} style={{ fontFamily: 'monospace', fontSize: 12 }} placeholder="WordPress admin username" />
              </div>
              <div className="form-field">
                <label>WP Application Password</label>
                <input type="password" value={editClient.wpAppPassword || ''} onChange={e => setEditClient(c => ({ ...c, wpAppPassword: e.target.value }))} style={{ fontFamily: 'monospace', fontSize: 12 }} placeholder="xxxx xxxx xxxx xxxx xxxx xxxx" />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Alternative to plugin key — generate in WP Admin → Users → Application Passwords. Clear the API Key above when using this.</div>
              </div>
              <div className="form-field">
                <label>Image Style</label>
                <select value={editClient.imageStyle || 'illustrated'} onChange={e => setEditClient(c => ({ ...c, imageStyle: e.target.value }))}>
                  <option value="illustrated">Illustrated</option>
                  <option value="watercolor">Watercolor & Ink</option>
                  <option value="realistic">Realistic Photo</option>
                </select>
              </div>
              <div className="form-field">
                <label>Publish Mode</label>
                <div
                  onClick={() => setEditClient(c => ({ ...c, autoPublish: !c.autoPublish }))}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ width: 44, height: 24, borderRadius: 12, background: editClient.autoPublish ? '#16a34a' : '#374151', position: 'relative', transition: 'background .2s' }}>
                    <div style={{ position: 'absolute', top: 3, left: editClient.autoPublish ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                  </div>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                    {editClient.autoPublish ? 'Publish immediately when generated' : 'Save as draft for review'}
                  </span>
                </div>
              </div>
              <div className="form-field">
                <label>Brand Voice Notes</label>
                <textarea value={editClient.brandVoice || ''} onChange={e => setEditClient(c => ({ ...c, brandVoice: e.target.value }))} style={{ minHeight: 80 }} placeholder="e.g. Family-owned, bilingual, straightforward tone..." />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditClient(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
